// src/lib/resources/benchmarks/compare.ts

/**
 * @file Benchmark comparison engine (pure, side-effect-free)
 * @description
 * Shared logic for comparing a user-supplied metric value against cited
 * industry benchmark ranges. Powers both the ICP calculators' "vs. industry
 * benchmark" output and the standalone ThruPlay Rate Benchmark tool
 * (see `docs/seo/2026-06-backlink-off-page-strategy.md`, Pillar 3 / May Play 1).
 *
 * Kept dependency-free and pure so the (fuzzy) percentile maths and verdict
 * banding are unit-testable without React, and so the same engine grounds
 * every surface that shows a benchmark — no drift between tools.
 *
 * The percentile is explicitly APPROXIMATE: real per-percentile distributions
 * aren't public, so we interpolate a smooth curve through the published
 * low / median / high anchors (25th / 50th / 75th). Surfaces should label it
 * "approx." — it is a directional band, not a precise rank.
 */

/** One cited benchmark band for a named audience/segment. */
export interface BenchmarkSegment {
  /** Stable id for code lookups, decoupled from the display label so tightening
   *  the user-facing `segment` copy can't silently break a consumer's lookup. */
  id?: string
  /** Human label, e.g. "15–30s, cold prospecting (Reels/Stories)". */
  segment: string
  /** Bottom of the cited typical range (lower marker anchor). */
  low: number
  /** Median of the cited typical range (centre marker anchor). */
  median: number
  /** Top of the cited typical range (upper marker anchor). */
  high: number
  /** Citation strings (publisher + year). Never empty. */
  sources: string[]
  /** Optional caveat (e.g. "directional", "mechanical completion"). */
  note?: string
  /** Optional 2-axis metadata (e.g. ThruPlay): creative-length bucket. */
  length?: string
  /** Optional 2-axis metadata (e.g. ThruPlay): audience temperature. */
  audience?: string
  /** Optional 2-axis metadata (Meta hook rate): placement bucket
   *  (Feed / Reels / Stories), which moves the hook band materially. */
  placement?: string
  /** Optional 2-axis metadata (paid-social CPM/CPC/CTR): stable platform id
   *  (e.g. "meta", "tiktok", "linkedin"). Lets one BenchmarkSet carry bands
   *  for several ad platforms and lets resolvers derive the platform list
   *  FROM the data, so a platform selector can never offer a platform the
   *  selected metric has no cited band for. */
  platform?: string
  /** Optional 2-axis metadata (paid-social CPM/CPC/CTR): campaign objective
   *  bucket where the cited source splits by objective (e.g. Meta "Traffic"
   *  vs "Leads"). Omitted when the source publishes one blended band. */
  objective?: string
  /** True when the band is a directional estimate, NOT a publicly-cited range
   *  — surfaces must flag it so it isn't read as equivalently sourced. */
  directional?: boolean
}

/** A full benchmark dataset for one metric. */
export interface BenchmarkSet {
  /** Stable id, e.g. "thruplay-rate". */
  id: string
  /** Display label, e.g. "ThruPlay Rate". */
  metricLabel: string
  /** How values render. */
  unit: 'ratio' | 'currency' | 'percent'
  /** True when a higher value is better (ROAS, ThruPlay); false for cost
   *  metrics where lower is better (CPM, CPV). Flips the verdict direction. */
  higherIsBetter: boolean
  /** The cited segment bands. */
  segments: BenchmarkSegment[]
  /** Canonical on-site definition (glossary), if any. */
  glossaryUrl?: string
  /** Deeper reference (blog benchmark post), if any. */
  referenceUrl?: string
}

/** Verdict bands, expressed in "goodness" space (already direction-corrected). */
export type BenchmarkVerdict = 'well-below' | 'below' | 'on-par' | 'above' | 'well-above'

/** Result of comparing one value to one segment. */
export interface BenchmarkComparison {
  segment: BenchmarkSegment
  value: number
  /**
   * Marker position in [0, 100] along the direction-corrected "goodness" axis,
   * used only to place the marker on the range track and pick a verdict band.
   * This is NOT a statistical percentile — the cited data is a typical *range*,
   * not a distribution, so it must never be presented as "Nth percentile" (a
   * value just outside the range is not "0th percentile"; plenty of viable
   * accounts operate outside a given segment's band).
   */
  position: number
  verdict: BenchmarkVerdict
  /** Short human label for the verdict. */
  verdictLabel: string
}

/** Finds a segment by exact label, or returns undefined. */
export function findSegment(set: BenchmarkSet, segmentLabel: string): BenchmarkSegment | undefined {
  return set.segments.find((s) => s.segment === segmentLabel)
}

/** Finds a segment by stable `id` (preferred over label-based lookup). */
export function findSegmentById(set: BenchmarkSet, id: string): BenchmarkSegment | undefined {
  return set.segments.find((s) => s.id === id)
}

/** Linear interpolation between two (x, y) points. */
function lerp(x: number, x0: number, y0: number, x1: number, y1: number): number {
  if (x1 === x0) return y0
  return y0 + ((x - x0) * (y1 - y0)) / (x1 - x0)
}

/**
 * Maps a raw value onto a 0–100 *marker position* through the published
 * anchors, where a larger value yields a larger output. Anchors: low→25,
 * median→50, high→75, with the curve extended symmetrically below `low` and
 * above `high` so values outside the typical range still produce a sensible
 * 0–100 reading (clamped). This is a track position for the marker, NOT a
 * statistical percentile.
 *
 * "raw" = larger value ⇒ larger number regardless of whether larger is good;
 * `compareToBenchmark` applies the direction flip.
 */
export function rawPosition(value: number, seg: BenchmarkSegment): number {
  // NaN flows through every comparison below as `false` and would fall out the
  // bottom as NaN; map it to 0 so callers never render a NaN width/position.
  // (±Infinity is handled correctly by the floor/ceil clamps below.)
  if (Number.isNaN(value)) return 0
  // Guard degenerate bands (zero-width spans) by nudging to a tiny epsilon.
  const lowSpan = Math.max(seg.median - seg.low, 1e-9)
  const highSpan = Math.max(seg.high - seg.median, 1e-9)
  const floor = seg.low - lowSpan // → 0
  const ceil = seg.high + highSpan // → 100

  if (value <= floor) return 0
  if (value >= ceil) return 100
  if (value <= seg.low) return clamp(lerp(value, floor, 0, seg.low, 25))
  if (value <= seg.median) return clamp(lerp(value, seg.low, 25, seg.median, 50))
  if (value <= seg.high) return clamp(lerp(value, seg.median, 50, seg.high, 75))
  return clamp(lerp(value, seg.high, 75, ceil, 100))
}

function clamp(n: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, n))
}

/** Human label per verdict (range-relative, never a percentile). */
const VERDICT_LABEL: Record<BenchmarkVerdict, string> = {
  'well-above': 'Well above typical range',
  above: 'Above typical range',
  'on-par': 'Within typical range',
  below: 'Below typical range',
  'well-below': 'Well below typical range',
}

/**
 * Cost-metric labels (`higherIsBetter: false`). Verdicts live in GOODNESS
 * space, so on a cost metric a $22 CPM against a $7–$15 band is verdict
 * 'well-below' — but labelling that "Well below typical range" reads as a
 * numeric position and inverts the user's understanding (their number is
 * numerically ABOVE the range). Cost labels therefore speak in cost terms:
 * good = cheaper than typical, poor = above typical cost.
 */
const COST_VERDICT_LABEL: Record<BenchmarkVerdict, string> = {
  'well-above': 'Well below typical cost',
  above: 'Below typical cost',
  'on-par': 'Within typical range',
  below: 'Above typical cost',
  'well-below': 'Well above typical cost',
}

/**
 * Classifies a value into a verdict using EXACT comparison to the cited
 * `low`/`high` band — so a value numerically outside the band can NEVER read
 * "Within typical range" (rounding the marker position previously flipped e.g.
 * 17.95 on an 18–28 band to on-par). The marker `position` is used only to pick
 * the "well-" magnitude tier once a value is already outside the band.
 */
export function verdictFor(
  set: BenchmarkSet,
  segment: BenchmarkSegment,
  value: number,
  position: number,
): BenchmarkVerdict {
  if (Number.isNaN(value)) return 'well-below'
  // Inclusive of the published anchors → within the typical range.
  // (±Infinity correctly falls through: it is never within the band, and the
  // `value > high` check below routes it to the right side.)
  if (value >= segment.low && value <= segment.high) return 'on-par'
  const aboveBand = value > segment.high
  // Direction-correct: above the band is "good" for higher-is-better metrics,
  // below the band is "good" for cost metrics.
  const goodSide = set.higherIsBetter ? aboveBand : !aboveBand
  if (goodSide) return position >= 92 ? 'well-above' : 'above'
  return position <= 8 ? 'well-below' : 'below'
}

/**
 * Compares `value` against one `segment` of `set`, returning a marker position
 * (for the range-band track) and a range-relative verdict derived from exact
 * boundary comparison. For cost metrics (`higherIsBetter: false`) the position
 * is inverted so a low cost reads as "above" (good).
 */
export function compareToBenchmark(
  set: BenchmarkSet,
  segment: BenchmarkSegment,
  value: number,
): BenchmarkComparison {
  const raw = rawPosition(value, segment)
  const position = Math.round(set.higherIsBetter ? raw : 100 - raw)
  const verdict = verdictFor(set, segment, value, position)
  const labels = set.higherIsBetter ? VERDICT_LABEL : COST_VERDICT_LABEL
  return { segment, value, position, verdict, verdictLabel: labels[verdict] }
}
